import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button, PageHeading, Spinner } from "@nu-assist/ui";
import { fetchPeople, triggerDriftCheck } from "./api";
import { PersonRow } from "./PersonRow";
import { AdminPersonPanel } from "./AdminPersonPanel";
import type { PersonStatus, PersonSummary } from "./types";

// ---------------------------------------------------------------------------
// Filter groups
// ---------------------------------------------------------------------------

type FilterGroup = "all" | "offboarding_pending" | "needs_onboarding" | "needs_review" | "informational";

const FILTER_GROUPS: { id: FilterGroup; label: string }[] = [
  { id: "all", label: "Everyone" },
  { id: "offboarding_pending", label: "Offboarding pending" },
  { id: "needs_onboarding", label: "Needs onboarding" },
  { id: "needs_review", label: "Needs review" },
  { id: "informational", label: "Informational" },
];

const GROUP_STATUSES: Record<Exclude<FilterGroup, "all">, PersonStatus[]> = {
  offboarding_pending: ["offboarding_pending"],
  needs_onboarding: ["needs_onboarding"],
  needs_review: ["alias_mismatch"],
  informational: ["on_leave", "leftover_seat", "not_a_person", "shared_identity", "never_signed_in"],
};

function groupCounts(people: PersonSummary[]): Record<FilterGroup, number> {
  const counts: Record<FilterGroup, number> = {
    all: people.length,
    offboarding_pending: 0,
    needs_onboarding: 0,
    needs_review: 0,
    informational: 0,
  };
  for (const p of people) {
    for (const [group, statuses] of Object.entries(GROUP_STATUSES) as [
      Exclude<FilterGroup, "all">,
      PersonStatus[],
    ][]) {
      if ((statuses as string[]).includes(p.status)) {
        counts[group]++;
        break;
      }
    }
  }
  return counts;
}

// ---------------------------------------------------------------------------
// Filter pill
// ---------------------------------------------------------------------------

function FilterPill({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "flex items-center gap-1.5 px-3 py-1 rounded-full text-label border transition-colors",
        active
          ? "border-accent text-accent bg-accent/10"
          : "border-border text-text hover:border-accent hover:text-accent",
      ].join(" ")}
    >
      {label}
      <span
        className={[
          "text-xs font-semibold rounded-full px-1.5 py-0.5 min-w-[20px] text-center",
          active ? "bg-accent text-bg" : "bg-surface text-text-secondary",
        ].join(" ")}
      >
        {count}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// System status dot
// ---------------------------------------------------------------------------

function SystemDot({ connected, label }: { connected: boolean | null; label: string }) {
  const color =
    connected === null ? "bg-yellow-400" : connected ? "bg-green-500" : "bg-red-500";
  return (
    <span className="flex items-center gap-1.5 text-label text-text-secondary">
      <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${color}`} />
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Table columns
// ---------------------------------------------------------------------------

const COLUMNS = ["Person", "Google", "Thrive", "HubSpot", "Nu-Assist", "Nu-Sync", "Status", "Actions"];

// ---------------------------------------------------------------------------
// Onboard modal
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function AdminPeoplePage() {
  const [filterGroup, setFilterGroup] = useState<FilterGroup>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["admin-people"],
    queryFn: fetchPeople,
    staleTime: 60_000,
    retry: false,
  });

  const driftCheck = useMutation({
    mutationFn: triggerDriftCheck,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-people"] });
    },
  });

  const people = data ?? [];
  const counts = groupCounts(people);

  const searchLower = searchQuery.toLowerCase().trim();
  const filtered = (
    filterGroup === "all"
      ? people
      : people.filter((p) => (GROUP_STATUSES[filterGroup] as string[]).includes(p.status))
  )
    .filter((p) => {
      if (!searchLower) return true;
      const nameMatch = (p.display_name ?? "").toLowerCase().includes(searchLower);
      const emailMatch = p.email.toLowerCase().includes(searchLower);
      const aliasMatch =
        Array.isArray(p.google_aliases) &&
        (p.google_aliases as string[]).some((a) => a.toLowerCase().includes(searchLower));
      return nameMatch || emailMatch || aliasMatch;
    })
    .slice()
    .sort((a, b) => {
      const cmp = (a.display_name ?? a.email).localeCompare(b.display_name ?? b.email);
      return sortOrder === "asc" ? cmp : -cmp;
    });

  const needsDecision = (counts.offboarding_pending ?? 0) + (counts.needs_onboarding ?? 0) + (counts.needs_review ?? 0);

  // Most recent per-person check OR the last drift run — whichever is newer.
  const lastCheckedAt = [
    ...people.map((p) => p.last_checked_at).filter(Boolean),
    driftCheck.data?.run_at ?? null,
  ]
    .filter(Boolean)
    .sort()
    .at(-1);

  const lastCheckedText = lastCheckedAt
    ? new Date(lastCheckedAt).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "never";

  // Side panel
  const selectedEmail = searchParams.get("person");
  const selectedPerson = selectedEmail
    ? (people.find((p) => p.email === selectedEmail) ?? null)
    : null;

  function openPanel(email: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("person", email);
      return next;
    });
  }

  function closePanel() {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("person");
      return next;
    });
  }

  return (
    <div className="min-h-full px-6 py-6 flex flex-col gap-5">
      {/* ------------------------------------------------------------------ */}
      {/* Header                                                               */}
      {/* ------------------------------------------------------------------ */}
      <div className="flex items-start justify-between gap-4">
        <PageHeading
          title="Staff members"
          description={
            <>
              Last checked {lastCheckedText}.{" "}
              {needsDecision > 0 ? (
                <span className="text-orange-500 font-medium">
                  {needsDecision} {needsDecision === 1 ? "person needs" : "people need"} a decision.
                </span>
              ) : (
                <span>No pending decisions.</span>
              )}
            </>
          }
        />
        <div className="flex items-center gap-2 shrink-0 mt-1">
          {driftCheck.data?.google_unreachable && (
            <span className="text-label text-orange-500">Google unreachable</span>
          )}
          {driftCheck.isError && (
            <span className="text-label text-error">Check failed</span>
          )}
          <Button
            variant="secondary"
            onClick={() => driftCheck.mutate()}
            disabled={driftCheck.isPending}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${driftCheck.isPending ? "animate-spin" : ""}`} aria-hidden="true" />
            {driftCheck.isPending ? "Checking…" : "Check now"}
          </Button>
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* System status bar                                                    */}
      {/* ------------------------------------------------------------------ */}
      {!isLoading && !error && (
        <div className="flex flex-wrap gap-4 pb-3 border-b border-border">
          <SystemDot
            connected={
              people.some((p) => p.google_suspended !== null)
                ? people.every((p) => p.google_suspended !== true)
                : null
            }
            label={
              people.some((p) => p.google_suspended !== null)
                ? `Google: ${people.filter((p) => p.google_suspended === false).length} active`
                : "Google: not checked"
            }
          />
          <SystemDot connected={true} label={`Thrive: ${people.length} users`} />
          <SystemDot
            connected={true}
            label={`HubSpot: ${people.filter((p) => p.hubspot_owner_archived !== null).length} users`}
          />
          <SystemDot connected={true} label={`Nu-Sync: ${people.length} rows`} />
          <SystemDot
            connected={true}
            label={`Nu-Assist: ${people.filter((p) => p.has_logged_in).length} logins`}
          />
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Attention banner — shown when drift alerts need action              */}
      {/* ------------------------------------------------------------------ */}
      {!isLoading && !error && (needsDecision > 0 || (driftCheck.data?.alerts ?? 0) > 0) && (
        <div className="flex items-start gap-3 rounded-md border border-orange-300 bg-orange-50 px-4 py-3 text-orange-900 dark:border-orange-700 dark:bg-orange-950 dark:text-orange-200">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-orange-500" aria-hidden="true" />
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">
              {needsDecision > 0
                ? `${needsDecision} ${needsDecision === 1 ? "person needs" : "people need"} attention`
                : `${driftCheck.data!.alerts} alert${driftCheck.data!.alerts === 1 ? "" : "s"} detected — refresh the list to see details`}
            </span>
            {needsDecision > 0 && (
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm">
                {(counts.needs_onboarding ?? 0) > 0 && (
                  <button
                    type="button"
                    className="underline underline-offset-2 hover:no-underline"
                    onClick={() => setFilterGroup("needs_onboarding")}
                  >
                    {counts.needs_onboarding} need onboarding
                  </button>
                )}
                {(counts.offboarding_pending ?? 0) > 0 && (
                  <button
                    type="button"
                    className="underline underline-offset-2 hover:no-underline"
                    onClick={() => setFilterGroup("offboarding_pending")}
                  >
                    {counts.offboarding_pending} offboarding pending
                  </button>
                )}
                {(counts.needs_review ?? 0) > 0 && (
                  <button
                    type="button"
                    className="underline underline-offset-2 hover:no-underline"
                    onClick={() => setFilterGroup("needs_review")}
                  >
                    {counts.needs_review} need review
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Search box                                                           */}
      {/* ------------------------------------------------------------------ */}
      {!isLoading && !error && (
        <div className="w-full max-w-sm">
          <input
            type="search"
            placeholder="Search name or any email…"
            className="w-full border border-border rounded-md px-3 py-1.5 text-body bg-bg text-text placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-accent"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Filter pills                                                         */}
      {/* ------------------------------------------------------------------ */}
      {!isLoading && !error && (
        <div className="flex flex-wrap gap-2">
          {FILTER_GROUPS.map((fg) => (
            <FilterPill
              key={fg.id}
              label={fg.label}
              count={counts[fg.id]}
              active={filterGroup === fg.id}
              onClick={() => setFilterGroup(fg.id)}
            />
          ))}
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Loading / error                                                      */}
      {/* ------------------------------------------------------------------ */}
      {isLoading && (
        <div className="flex items-center gap-2 py-8">
          <Spinner size="sm" />
          <span className="text-body text-text-secondary">Loading people…</span>
        </div>
      )}

      {error && !isLoading && (
        <p className="text-body text-error py-4">
          Failed to load people.{" "}
          <button type="button" className="underline" onClick={() => refetch()}>
            Retry
          </button>
        </p>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Table                                                                */}
      {/* ------------------------------------------------------------------ */}
      {!isLoading && !error && (
        <div className="overflow-x-auto rounded-md border border-border bg-table-surface">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-border text-left">
                {COLUMNS.map((col) => (
                  <th key={col} scope="col" className="text-label font-semibold text-text-secondary">
                    {col === "Person" ? (
                      <button
                        type="button"
                        onClick={() => setSortOrder((o) => (o === "asc" ? "desc" : "asc"))}
                        className="flex items-center gap-1 px-3 py-2 hover:text-text transition-colors select-none"
                      >
                        {col}
                        <span className="text-[10px] leading-none">{sortOrder === "asc" ? "▲" : "▼"}</span>
                      </button>
                    ) : (
                      <span className="block px-3 py-2">{col}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((person) => (
                <PersonRow
                  key={person.email}
                  person={person}
                  onRefresh={() => refetch()}
                  onPersonClick={() => openPanel(person.email)}
                />
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length} className="py-10 text-center text-body text-text-secondary">
                    No people match this filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Side panel                                                           */}
      {/* ------------------------------------------------------------------ */}
      {selectedPerson && (
        <AdminPersonPanel person={selectedPerson} onClose={closePanel} />
      )}

    </div>
  );
}
